import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, Users, MapPin, DollarSign, Sparkles, Shield, Star, Check, MessageCircle, Award, Zap } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import BookingTimeline from "../components/BookingTimeline";

/**
 * CustomerBriefDetail — Phase 46.
 *
 * Read-only deep view of a single TREL Request Brief plus the comparison of
 * provider offers received. Customer can "Choose this Concierge" to mint a
 * booking with the brief context attached.
 */
export default function CustomerBriefDetail() {
  const { briefId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [selecting, setSelecting] = useState(null);
  const [linkedBooking, setLinkedBooking] = useState(null);

  // Initial brief fetch. Wrapped in an async helper with cancel-flag so
  // setState happens inside an async callback (avoids the false-positive
  // react-hooks/set-state-in-effect rule).
  useEffect(() => {
    let alive = true;
    const fetchBrief = async () => {
      try {
        const r = await api.get(`/briefs/${briefId}`);
        if (alive) setBrief(r.data);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "Could not load brief.");
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchBrief();
    return () => { alive = false; };
  }, [briefId]);

  // Manual reload used after selecting an offer.
  const reloadBrief = async () => {
    try {
      const r = await api.get(`/briefs/${briefId}`);
      setBrief(r.data);
    } catch (_) { /* preserve last good state */ }
  };

  // Resolve the customer's booking when a concierge has been selected so we
  // can render the canonical 7-stage TREL workflow timeline. Fetched
  // through the existing customer-scoped /bookings endpoint — no new API.
  const selectedBookingId = brief?.selected_booking_id;
  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!selectedBookingId) {
        if (alive) setLinkedBooking(null);
        return;
      }
      try {
        const r = await api.get("/bookings");
        if (!alive) return;
        const match = (r.data || []).find((b) => b.booking_id === selectedBookingId);
        if (alive) setLinkedBooking(match || null);
      } catch (_) {
        if (alive) setLinkedBooking(null);
      }
    };
    load();
    return () => { alive = false; };
  }, [selectedBookingId]);

  const onSelect = async (offerId) => {
    if (!window.confirm("Choose this Concierge? We'll prepare your booking with the full brief.")) return;
    setSelecting(offerId);
    try {
      const r = await api.post(`/briefs/${briefId}/select/${offerId}`);
      const bookingId = r.data?.booking?.booking_id;
      if (bookingId) {
        nav(`/customer?booked=${bookingId}`);
      } else {
        reloadBrief();
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not select offer.");
      setSelecting(null);
    }
  };

  if (loading) {
    return <div className="max-w-4xl mx-auto px-6 py-16 text-stone-500">Loading brief…</div>;
  }
  if (err || !brief) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-16">
        <Link to="/customer" className="inline-flex items-center gap-1.5 text-sm text-stone-600 hover:text-stone-900">
          <ArrowLeft size={14}/> Back to dashboard
        </Link>
        <p className="mt-6 text-stone-700">{err || "Brief not found."}</p>
      </div>
    );
  }

  const offers = brief.offers || [];
  const isPreview = !!user?.preview_account;
  const isOpen = brief.status === "open";

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-6 py-10 pb-32 lg:pb-12" data-testid="customer-brief-detail">
      <Link to="/customer" className="inline-flex items-center gap-1.5 text-sm text-stone-600 hover:text-stone-900">
        <ArrowLeft size={14}/> Back to dashboard
      </Link>

      {/* TREL Workflow timeline — visible once a concierge has been selected
          (booking minted). Walks the customer through the canonical seven
          stages so they always know where they are. */}
      {linkedBooking && (
        <div className="mt-6">
          <BookingTimeline booking={linkedBooking} />
        </div>
      )}

      {/* BRIEF SUMMARY */}
      <section className="mt-6 rounded-3xl border border-stone-200 bg-white p-6 sm:p-8 shadow-[0_1px_2px_rgba(7,26,45,0.05),0_18px_44px_-24px_rgba(7,26,45,0.18)]">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-2xl bg-[#00AEEF]/10 text-[#00AEEF] flex items-center justify-center shrink-0">
            <Sparkles size={22}/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#064E3B]">TREL Request Brief</span>
              <span
                data-testid="brief-status"
                className={`text-[11px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full ${
                  isOpen
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    : "bg-stone-100 text-stone-700 border border-stone-200"
                }`}
              >
                {brief.status}
              </span>
            </div>
            <h1 className="mt-2 font-serif text-2xl sm:text-3xl tracking-tight text-[#071A2D] leading-tight">
              {brief.request}
            </h1>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Meta icon={MapPin}    label="Where" value={brief.city || "—"}/>
          <Meta icon={Calendar}  label="When"  value={brief.date ? new Date(brief.date).toLocaleDateString(undefined, { month:"short", day:"numeric", year:"numeric" }) : (brief.flexible_date ? "Flexible" : "—")}/>
          <Meta icon={Users}     label="Guests" value={`${brief.guests || 2}`}/>
          <Meta icon={DollarSign} label="Budget" value={brief.budget_max ? `Up to $${Number(brief.budget_max).toLocaleString()}` : "Open"}/>
        </div>

        {brief.ai_summary && (
          <div className="mt-5 rounded-2xl bg-[#FFF8EE] border border-[#C9A45C]/35 p-4 sm:p-5">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#C9A45C]">AI Summary</div>
            <p className="mt-2 text-[14.5px] text-[#071A2D] leading-relaxed">{brief.ai_summary}</p>
          </div>
        )}

        {!!(brief.interests || []).length && (
          <div className="mt-5">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#071A2D]/55">Interests</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {brief.interests.map((s) => (
                <span key={s} className="text-[12.5px] rounded-full bg-stone-100 px-3 py-1 text-[#071A2D]/80">{s}</span>
              ))}
            </div>
          </div>
        )}

        {brief.important_details && (
          <div className="mt-5">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#071A2D]/55">Important details</div>
            <p className="mt-2 text-[14px] text-[#071A2D]/80 leading-relaxed">{brief.important_details}</p>
          </div>
        )}
      </section>

      {/* OFFERS */}
      <section className="mt-8" data-testid="brief-offers">
        <div className="flex items-baseline justify-between">
          <h2 className="font-serif text-2xl text-[#071A2D]">Concierge offers</h2>
          <span className="text-sm text-stone-500">{offers.length} {offers.length === 1 ? "offer" : "offers"} received</span>
        </div>

        {/* External costs notice — clarifies the canonical TREL payment model.
            The customer pays their concierge's planning + coordination fee.
            External arrangements (hotels, restaurants, transport, vendors,
            tickets) are coordinated by the concierge and paid directly. */}
        <div
          data-testid="brief-fee-notice"
          className="mt-4 rounded-2xl bg-[#FFF8EE] border border-[#C9A45C]/30 px-4 py-3 sm:px-5 sm:py-4 flex items-start gap-3"
        >
          <Shield size={16} className="text-[#064E3B] shrink-0 mt-0.5"/>
          <div className="text-[12.5px] sm:text-[13px] text-[#071A2D]/80 leading-relaxed">
            <span className="font-semibold text-[#071A2D]">You&apos;re paying the concierge service fee.</span>{" "}
            Your concierge handles the planning, coordination, and details. External costs
            (hotels, restaurants, transport, vendors, tickets) are arranged with your
            concierge and paid separately.
          </div>
        </div>

        {offers.length === 0 ? (
          <div className="mt-5 rounded-2xl border border-dashed border-stone-300 bg-white p-8 text-center">
            <Sparkles size={20} className="mx-auto text-[#00AEEF]"/>
            <p className="mt-3 text-stone-700">Your brief is open. We&apos;ll notify you the moment a verified Concierge sends an offer.</p>
          </div>
        ) : (
          <div className="mt-5 grid sm:grid-cols-2 gap-4">
            {offers.map((o) => (
              <OfferCard
                key={o.offer_id}
                offer={o}
                disabled={!isOpen || selecting !== null || isPreview}
                selecting={selecting === o.offer_id}
                onSelect={() => onSelect(o.offer_id)}
              />
            ))}
          </div>
        )}

        {isPreview && offers.length > 0 && (
          <p className="mt-4 text-xs text-stone-500 italic">
            Preview Mode — selecting a Concierge is disabled. Sign up to book live.
          </p>
        )}
      </section>
    </div>
  );
}

function Meta({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-stone-200 bg-stone-50 px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wide font-semibold text-stone-500">
        <Icon size={12}/> {label}
      </div>
      <div className="mt-0.5 text-[14px] font-medium text-[#071A2D] truncate">{value}</div>
    </div>
  );
}

function OfferCard({ offer, onSelect, disabled, selecting }) {
  const p = offer.provider || {};
  return (
    <article
      data-testid={`offer-card-${offer.offer_id}`}
      className="rounded-2xl border border-stone-200 bg-white p-5 shadow-[0_1px_2px_rgba(7,26,45,0.05),0_18px_44px_-26px_rgba(7,26,45,0.16)] hover:shadow-[0_2px_4px_rgba(7,26,45,0.06),0_24px_56px_-26px_rgba(7,26,45,0.22)] transition-all duration-200"
    >
      <div className="flex items-center gap-3">
        {p.photo ? (
          <img src={p.photo} alt="" className="w-12 h-12 rounded-full object-cover"/>
        ) : (
          <div className="w-12 h-12 rounded-full bg-[#C9A45C]/15 flex items-center justify-center text-[#C9A45C] font-semibold">
            {(p.name || "?").slice(0,1).toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-medium text-[#071A2D] truncate flex items-center gap-1.5">
            {p.name}
            {p.trel_certified && <Shield size={13} className="text-[#064E3B]" title="TREL Certified"/>}
            {p.elite_badge_active && <Award size={13} className="text-[#C9A45C]" title="TREL Elite"/>}
          </div>
          <div className="text-xs text-stone-500 truncate flex items-center gap-1">
            <Star size={11} className="text-[#C9A45C]"/> {p.rating || "—"} · {p.moments_created || 0} moments
            {p.city && <> · {p.city}</>}
          </div>
          {/* Phase 47 — "Enhanced Concierge" badge. Surfaces TREL+ providers
              as a trust/info signal without promoting their offer above
              non-TREL+ peers (no positional boost). */}
          {p.trel_plus_active && (
            <span
              data-testid={`offer-enhanced-${offer.offer_id}`}
              className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-[#C9A45C]/12 text-[#8c6f33] border border-[#C9A45C]/35 px-2 py-0.5 text-[10.5px] uppercase tracking-[0.16em] font-semibold"
              title="Verified concierge with TREL+ business tools"
            >
              <Zap size={10}/> Enhanced Concierge
            </span>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-wide text-stone-500 font-semibold">Concierge fee · from</div>
          <div className="text-[#071A2D] font-semibold tabular-nums" data-testid={`offer-price-${offer.offer_id}`}>
            ${Number(offer.starting_estimate).toLocaleString()}
          </div>
          <div className="text-[9.5px] uppercase tracking-wide text-stone-400 mt-0.5">
            Planning + coordination
          </div>
        </div>
      </div>

      <p className="mt-4 text-[13.5px] text-[#071A2D]/80 leading-relaxed">
        {offer.message}
      </p>

      {offer.approach && (
        <details className="mt-3 group">
          <summary className="text-[12px] font-medium text-[#00AEEF] cursor-pointer select-none">
            See approach
          </summary>
          <p className="mt-2 text-[13px] text-[#071A2D]/75 leading-relaxed whitespace-pre-line">{offer.approach}</p>
        </details>
      )}

      <div className="mt-5 flex items-center gap-2">
        <Link
          to={`/concierge/${p.user_id || ""}`}
          data-testid={`offer-view-${offer.offer_id}`}
          className="inline-flex items-center gap-1.5 rounded-full border border-stone-200 bg-white px-4 py-2 text-[12.5px] font-medium text-[#071A2D] hover:bg-stone-50"
        >
          <MessageCircle size={13}/> View Details
        </Link>
        <button
          type="button"
          disabled={disabled}
          data-testid={`offer-choose-${offer.offer_id}`}
          onClick={onSelect}
          className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#FF7A45] text-white px-4 py-2 text-[12.5px] font-semibold hover:brightness-105 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {selecting ? "Selecting…" : (<><Check size={13}/> Choose this Concierge</>)}
        </button>
      </div>
    </article>
  );
}
